import { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Map from "@/components/Map";
import backgroundNoise from "@/assets/background-noise.svg";
import { supabase } from "@/integrations/supabase/client";

const Contact = () => {
  const { toast } = useToast();
  const heroReveal = useScrollReveal();
  const contactReveal = useScrollReveal();
  const faqReveal = useScrollReveal();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    budget: "",
    timeline: "",
    message: ""
  });

  const [newsletterEmail, setNewsletterEmail] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      // Create detailed message with all form fields
      const detailedMessage = `
${formData.message}

Additional Information:
Service: ${formData.service || 'Not specified'}
Budget: ${formData.budget || 'Not specified'}
Timeline: ${formData.timeline || 'Not specified'}
      `.trim();

      console.log('Attempting to insert lead:', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        company: formData.company || null,
        message: detailedMessage,
      });

      const { data, error } = await supabase.from('leads').insert({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        company: formData.company || null,
        message: detailedMessage,
        status: 'new'
      });

      console.log('Insert result:', { data, error });

      if (error) {
        console.error('Insert error details:', error);
        throw error;
      }

      toast({
        title: "Message Sent!",
        description: "We'll respond within 24 hours."
      });

      // Reset form
      setFormData({ 
        name: "", 
        email: "", 
        company: "",
        phone: "",
        service: "",
        budget: "",
        timeline: "",
        message: "" 
      });
    } catch (error: any) {
      console.error('Caught error:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newsletterEmail || !newsletterEmail.includes("@")) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }

    try {
      console.log('Attempting newsletter subscription:', newsletterEmail);

      const { data, error } = await supabase.from('leads').insert({
        name: newsletterEmail.split('@')[0], // Use email username as name
        email: newsletterEmail,
        phone: null,
        company: null,
        message: "Newsletter subscription request",
        status: 'new'
      });

      console.log('Newsletter insert result:', { data, error });

      if (error) {
        console.error('Newsletter insert error:', error);
        throw error;
      }

      toast({
        title: "Subscribed!",
        description: "You'll receive our newsletter soon."
      });

      setNewsletterEmail("");
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive"
      });
    }
  };

  const faqs = [
    {
      question: "What services do you offer?",
      answer: "We provide customised digital marketing solutions designed to help your business grow. Our services include social media strategy, website and app development, and the integration of AI tools for marketing. Each service is tailored to align with your brand and business objectives."
    },
    {
      question: "How much does it cost?",
      answer: "We offer flexible packages and fixed-fee options so you always know what you're paying for upfront. Pricing depends on the scope of your project, and we're happy to provide a clear quote after an initial chat."
    },
    {
      question: "How does the process work?",
      answer: "Our process begins with a free consultation to understand your specific needs. From there, we set goals, agree on a strategy, and deliver on the project with regular updates, allowing you to track results."
    },
    {
      question: "Do you have examples of your work?",
      answer: "Yes. We've supported businesses with campaigns that boosted their visibility, improved engagement, and increased sales. Case studies and examples can be shared on request."
    },
    {
      question: "Do you work with small businesses and startups?",
      answer: "Absolutely. We specialise in helping small businesses grow sustainably, offering affordable and practical solutions that make a big impact without overwhelming budgets."
    },
    {
      question: "How can I get in touch?",
      answer: "You can call us directly, email us, or reach out through our social media channels. We aim to respond quickly and are always happy to answer your questions."
    },
    {
      question: "Do you share marketing tips or resources?",
      answer: "Yes! You can subscribe to our email list for exclusive insights, tips, and resources that help your business grow sustainably."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-4 pt-24 md:pt-32 pb-12 md:pb-20 bg-foreground text-background overflow-hidden"
        style={{
          backgroundImage: `url(${backgroundNoise})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div 
          ref={heroReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            heroReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Get in <span className="text-accent italic">Touch</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-background/80 leading-relaxed max-w-3xl mx-auto">
              We'd be delighted to hear from you. Let's discuss how we can help your business grow.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20 px-4">
        <div 
          ref={contactReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            contactReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Left Column - Contact Info */}
            <div className="lg:col-span-1">
              {/* Contact Info Card */}
              <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-sm space-y-6 h-full">
                <div>
                  <h3 className="font-semibold text-lg md:text-xl mb-4">Contact Information</h3>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-sm mb-1">Address</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Level 30, The Leadenhall Building<br />
                        122 Leadenhall Street<br />
                        London, EC3V 4AB
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone size={20} className="text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-sm mb-1">Phone</p>
                      <a href="tel:+442037535435" className="text-sm text-muted-foreground hover:text-accent transition-colors">
                        +44 203 753 5435
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail size={20} className="text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-sm mb-1">Email</p>
                      <a href="mailto:inquiry@northquestmarketing.co.uk" className="text-sm text-muted-foreground hover:text-accent transition-colors break-all">
                        inquiry@northquestmarketing.co.uk
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock size={20} className="text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-sm mb-1">Hours</p>
                      <p className="text-sm text-muted-foreground">
                        Monday – Friday<br />
                        9:00 am – 5:00 pm
                      </p>
                    </div>
                  </div>
                </div>

                <Button variant="outline" className="w-full" asChild>
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=The+Leadenhall+Building+122+Leadenhall+Street+London" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Get Directions
                  </a>
                </Button>
              </div>
            </div>

            {/* Right Column - Contact Form & Newsletter */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Form Card */}
              <div 
                className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-lg"
                style={{
                  backgroundImage: `linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--muted)) 100%)`
                }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                    <Send size={24} className="text-accent-foreground" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold">Send Us a Message</h2>
                    <p className="text-sm text-muted-foreground">We'll respond within 24 hours</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm md:text-base font-medium mb-2">
                        Your Name *
                      </label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="text-sm md:text-base bg-background"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm md:text-base font-medium mb-2">
                        Your Email *
                      </label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="text-sm md:text-base bg-background"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="company" className="block text-sm md:text-base font-medium mb-2">
                        Company Name
                      </label>
                      <Input
                        id="company"
                        type="text"
                        placeholder="Your Company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="text-sm md:text-base bg-background"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm md:text-base font-medium mb-2">
                        Phone Number
                      </label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+44 123 456 7890"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="text-sm md:text-base bg-background"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm md:text-base font-medium mb-2">
                      Service Interested In
                    </label>
                    <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })}>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="website-marketing">Website Marketing</SelectItem>
                        <SelectItem value="mobile-app">Mobile App Services</SelectItem>
                        <SelectItem value="ai-marketing">AI for Marketing</SelectItem>
                        <SelectItem value="social-media">Social Media Services</SelectItem>
                        <SelectItem value="digital-marketing">Digital Marketing</SelectItem>
                        <SelectItem value="not-sure">Not Sure Yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="budget" className="block text-sm md:text-base font-medium mb-2">
                        Budget Range
                      </label>
                      <Select value={formData.budget} onValueChange={(value) => setFormData({ ...formData, budget: value })}>
                        <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Select budget" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="under-5k">Under £5,000</SelectItem>
                          <SelectItem value="5k-10k">£5,000 - £10,000</SelectItem>
                          <SelectItem value="10k-25k">£10,000 - £25,000</SelectItem>
                          <SelectItem value="25k-50k">£25,000 - £50,000</SelectItem>
                          <SelectItem value="50k-plus">£50,000+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label htmlFor="timeline" className="block text-sm md:text-base font-medium mb-2">
                        Project Timeline
                      </label>
                      <Select value={formData.timeline} onValueChange={(value) => setFormData({ ...formData, timeline: value })}>
                        <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Select timeline" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="urgent">Urgent (Within 1 month)</SelectItem>
                          <SelectItem value="1-3-months">1-3 Months</SelectItem>
                          <SelectItem value="3-6-months">3-6 Months</SelectItem>
                          <SelectItem value="6-plus-months">6+ Months</SelectItem>
                          <SelectItem value="flexible">Flexible</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm md:text-base font-medium mb-2">
                      Tell Us About Your Project *
                    </label>
                    <Textarea
                      id="message"
                      rows={6}
                      placeholder="Describe your goals, challenges, and what you're looking to achieve..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="text-sm md:text-base bg-background"
                      required
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full text-sm md:text-base bg-accent text-accent-foreground hover:bg-accent/90">
                    Send Message
                  </Button>
                </form>
              </div>

              {/* Newsletter Card */}
              <div 
                className="relative overflow-hidden bg-foreground text-background rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl"
                style={{
                  backgroundImage: `url(${backgroundNoise})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                      <Mail size={24} className="text-accent-foreground" />
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold">Stay Updated</h3>
                      <p className="text-sm text-background/80">Join our newsletter</p>
                    </div>
                  </div>
                  <p className="text-sm md:text-base text-background/80 mb-6">
                    Get exclusive digital marketing insights, tips, and strategies delivered straight to your inbox.
                  </p>
                  <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3">
                    <Input 
                      type="email" 
                      placeholder="your@email.com"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      className="text-sm md:text-base bg-background text-foreground flex-1"
                      required
                    />
                    <Button type="submit" size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 whitespace-nowrap">
                      Subscribe
                    </Button>
                  </form>
                  <p className="text-xs text-background/60 mt-4">
                    No spam. Unsubscribe anytime.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-20 px-4 bg-muted/20">
        <div 
          ref={faqReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            faqReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8 md:mb-12 text-center">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-card border border-border rounded-2xl md:rounded-3xl px-5 md:px-6">
                  <AccordionTrigger className="text-left font-semibold text-sm md:text-base py-4 md:py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm md:text-base">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
